import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Check, Loader2, Crown, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { partyApi, getPlayer } from "@/lib/party";

const COLORS = ["#00F0FF", "#FF003C", "#39FF14", "#EFFF04"];

export default function PartyPlay() {
  const { code } = useParams();
  const navigate = useNavigate();
  const me = getPlayer(code);
  const [s, setS] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const dead = useRef(false);

  useEffect(() => {
    if (!me) { navigate(`/party?code=${code}`); return; }
    dead.current = false;
    const poll = async () => {
      if (dead.current) return;
      try {
        const data = await partyApi.state(code, { pid: me.pid });
        if (data.rejoin) { dead.current = true; navigate(`/party?code=${code}`); return; }
        setS(data);
      } catch (e) {
        if (e?.response?.status === 404) { dead.current = true; navigate("/party"); }
      }
    };
    poll();
    const id = setInterval(poll, 1200);
    return () => { dead.current = true; clearInterval(id); };
  }, [code]); // eslint-disable-line

  if (!s) return <div className="min-h-[70vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-neon-lime" /></div>;
  const ph = s.phase || {};

  const answer = async (payload) => { setBusy(true); try { await partyApi.answer(code, me.pid, me.token, payload); } catch (e) { toast.error(e?.response?.data?.detail || "Try again"); } finally { setBusy(false); } };
  const vote = async (id) => { setBusy(true); try { await partyApi.vote(code, me.pid, me.token, id); } catch (e) { toast.error(e?.response?.data?.detail || "Try again"); } finally { setBusy(false); } };

  return (
    <div className="max-w-md mx-auto px-4 py-8 min-h-[80vh] flex flex-col" data-testid="party-play">
      <div className="flex items-center gap-2 mb-6">
        <span className="text-3xl">{me.avatar}</span>
        <div className="flex-1 min-w-0">
          <p className="font-display font-bold text-lg leading-none truncate">{me.name}</p>
          <p className="text-xs text-zinc-500 font-mono">Room {code}</p>
        </div>
        {s.you && <span className="font-mono text-neon-lime font-bold" data-testid="my-score">{s.you.score}</span>}
      </div>

      <div className="flex-1 flex flex-col justify-center">
        {/* LOBBY */}
        {s.status === "lobby" && (
          <div className="text-center" data-testid="play-lobby">
            <Check className="w-10 h-10 text-neon-lime mx-auto mb-3" />
            <p className="font-display font-black text-2xl uppercase">You're in!</p>
            <p className="text-zinc-400 mt-2">Watch the host screen. The game starts soon…</p>
          </div>
        )}

        {/* TRIVIA QUESTION */}
        {s.status === "question" && (
          ph.you_answered ? (
            <Locked text="Answer locked in!" sub="Watch the host screen for the reveal." />
          ) : (
            <div data-testid="play-question">
              <p className="text-center font-display font-bold text-lg mb-5 leading-tight">{ph.q}</p>
              <div className="grid grid-cols-1 gap-3">
                {ph.options.map((o, i) => (
                  <button key={i} disabled={busy} onClick={() => answer({ choice: i })} data-testid={`answer-${i}`}
                    className="flex items-center gap-3 rounded-xl px-4 py-5 font-display font-bold text-left text-black active:scale-95 transition-transform disabled:opacity-60"
                    style={{ background: COLORS[i] }}>
                    <span className="w-7 h-7 rounded-md bg-black/20 flex items-center justify-center font-black">{"ABCD"[i]}</span>
                    {o}
                  </button>
                ))}
              </div>
            </div>
          )
        )}

        {/* TRIVIA REVEAL */}
        {s.status === "reveal" && s.mode === "trivia" && (() => {
          const mine = ph.results?.find((r) => r.pid === me.pid);
          const ok = mine?.correct;
          return (
            <div className="text-center" data-testid="play-reveal">
              <p className={`font-display font-black text-3xl uppercase ${ok ? "text-neon-lime" : "text-neon-pink"}`}>{ok ? "Correct!" : "Nope!"}</p>
              {ok && <p className="text-neon-lime font-mono text-xl mt-2">+{mine.gained}</p>}
              <p className="text-zinc-400 mt-4 font-mono text-sm">Your score: {s.you?.score}</p>
            </div>
          );
        })()}

        {/* PUNCHLINE ANSWERING */}
        {s.status === "answering" && (
          ph.you_submitted ? (
            <Locked text="Answer submitted!" sub="Get ready to vote on the funniest." />
          ) : (
            <div data-testid="play-answering">
              <p className="text-center text-neon-pink uppercase tracking-widest text-xs font-bold mb-2">Your funniest answer</p>
              <p className="text-center font-display font-bold text-xl mb-5 leading-tight">{ph.prompt}</p>
              <textarea value={text} onChange={(e) => setText(e.target.value.slice(0, 100))} data-testid="answer-text"
                rows={3} placeholder="Type something hilarious…"
                className="w-full bg-zinc-950 border-2 border-zinc-700 focus:border-neon-pink outline-none rounded-xl px-4 py-3 text-white text-lg resize-none" />
              <Button disabled={busy || !text.trim()} onClick={() => answer({ text })} data-testid="answer-submit"
                className="w-full mt-3 bg-neon-pink text-white rounded-none font-bold uppercase tracking-widest h-12 hover:bg-white hover:text-black disabled:opacity-50">
                {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Submit <Send className="w-4 h-4 ml-2" /></>}
              </Button>
            </div>
          )
        )}

        {/* PUNCHLINE VOTING */}
        {s.status === "voting" && (
          ph.you_voted ? (
            <Locked text="Vote cast!" sub="See the results on the host screen." />
          ) : (
            <div data-testid="play-voting">
              <p className="text-center text-neon-cyan uppercase tracking-widest text-xs font-bold mb-2">Vote for the funniest</p>
              <p className="text-center font-display font-bold text-lg mb-5 leading-tight">{ph.prompt}</p>
              <div className="grid gap-3">
                {ph.answers.map((a) => (
                  <button key={a.id} disabled={busy || a.own} onClick={() => vote(a.id)} data-testid={`vote-${a.id}`}
                    className={`rounded-xl px-4 py-4 font-display font-bold text-left border-2 transition-colors ${a.own ? "border-zinc-800 bg-zinc-900 text-zinc-600" : "border-zinc-700 bg-zinc-950 hover:border-neon-cyan text-white active:scale-95"}`}>
                    {a.text} {a.own && <span className="text-xs font-mono ml-1">(your answer)</span>}
                  </button>
                ))}
              </div>
            </div>
          )
        )}

        {/* PUNCHLINE REVEAL */}
        {s.status === "reveal" && s.mode === "punchline" && (() => {
          const mine = ph.results?.find((r) => r.pid === me.pid);
          return (
            <div className="text-center" data-testid="play-reveal">
              <p className="font-display font-black text-2xl uppercase">Round over!</p>
              {mine ? <p className="text-neon-lime font-mono text-xl mt-3">+{mine.gained} · {mine.votes} vote{mine.votes === 1 ? "" : "s"}</p>
                    : <p className="text-zinc-400 mt-3">No answer this round.</p>}
              <p className="text-zinc-400 mt-3 font-mono text-sm">Your score: {s.you?.score}</p>
            </div>
          );
        })()}

        {/* ENDED */}
        {s.status === "ended" && (() => {
          const rank = (ph.final || []).findIndex((r) => r.pid === me.pid) + 1;
          return (
            <div className="text-center" data-testid="play-ended">
              {rank === 1 && <Crown className="w-10 h-10 text-neon-yellow mx-auto mb-2" />}
              <p className="font-display font-black text-3xl uppercase">{rank === 1 ? "You won! 🎉" : `You placed #${rank}`}</p>
              <p className="text-zinc-400 mt-2 font-mono">Final score: {s.you?.score}</p>
              <Button onClick={() => navigate("/party")} className="mt-6 bg-neon-lime text-black rounded-none font-bold uppercase tracking-widest h-11 px-6">Play Again</Button>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

const Locked = ({ text, sub }) => (
  <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center" data-testid="play-locked">
    <Check className="w-12 h-12 text-neon-lime mx-auto mb-3" />
    <p className="font-display font-black text-2xl uppercase">{text}</p>
    <p className="text-zinc-400 mt-2">{sub}</p>
  </motion.div>
);
